import {useEffect} from 'react'

/**
 * Returns the largest scalar in a Chakra-style responsive object, or the value
 * itself if it's already scalar. Used to size the clone bands so the widest
 * breakpoint always has a full screen of cloned slides on each side.
 */
export const maxResponsive = (responsive) => {
    if (typeof responsive !== 'object' || responsive === null) {
        const n = Number(responsive)
        return Number.isFinite(n) && n > 0 ? n : 1
    }
    const values = Object.values(responsive)
        .map(Number)
        .filter((n) => Number.isFinite(n) && n > 0)
    return values.length ? Math.max(...values) : 1
}

/**
 * Compute the start/end of the "real" slide region in the scroll track.
 * Reads DOM offsets so this is correct regardless of the styling approach
 * (responsive widths, gaps, etc.). Returns null when the markers aren't yet
 * rendered (initial layout pass) — caller should retry next frame.
 */
const computeBoundaries = (node, slidesLength) => {
    const firstReal = node.querySelector('[data-real-index="0"]')
    const lastReal = node.querySelector(`[data-real-index="${slidesLength - 1}"]`)
    if (!firstReal || !lastReal) return null
    const realStart = firstReal.offsetLeft
    /* Boundary used for "past the end" detection: the offset of the slide
     * immediately AFTER the last real slide (i.e. start of the tail clone
     * region). Falls back to last-real-end when no tail clone exists. */
    const afterReal = lastReal.nextElementSibling
    const realEnd = afterReal
        ? afterReal.offsetLeft
        : lastReal.offsetLeft + lastReal.offsetWidth
    return {realStart, realEnd, realRangeWidth: realEnd - realStart}
}

/**
 * Seamless-loop scroll behavior for a horizontally-scrolling carousel that
 * renders cloned slides at both ends.
 *
 * Caller is responsible for rendering the augmented slide list and tagging
 * each REAL slide with `data-real-index="N"` (0..slidesLength-1). Clones at
 * the head and tail must NOT carry that attribute. This hook then:
 *   - Sets initial `scrollLeft` to the first real slide on mount and on resize.
 *   - Listens to scroll events; when the position crosses into a clone region
 *     it instantly re-snaps `scrollLeft` to the equivalent position inside the
 *     real region (`behavior: 'auto'`) so the user sees no rewind animation.
 *
 * @param {React.RefObject<HTMLElement>} scrollRef - Scroll container.
 * @param {{enabled: boolean, slidesLength: number}} options
 */
export const useInfiniteLoop = (scrollRef, {enabled, slidesLength}) => {
    useEffect(() => {
        if (!enabled || slidesLength <= 0) return undefined
        const node = scrollRef.current
        if (!node) return undefined

        let snapping = false

        const setInitial = () => {
            const b = computeBoundaries(node, slidesLength)
            if (!b) return
            if (snapping) return
            /* Only anchor when the user hasn't scrolled yet (layout-ready or
             * just-mounted state). Resizes after the user has interacted
             * preserve their position rather than yanking back to slide 0. */
            if (node.scrollLeft === 0) {
                /* Subtract the container's left padding so tile 1 lands at
                 * the padded position (i.e. respects the scroll-padding /
                 * snap edge), not flush against the scroll container's
                 * inner-left edge. Critical for carousels that bleed past
                 * the page gutter via `padding-left: calc(50vw - 50%)`:
                 * without this offset, the initial jump would scroll past
                 * the gutter and clip tile 1 against the viewport edge. */
                const padLeft =
                    parseFloat(getComputedStyle(node).paddingLeft) || 0
                node.scrollLeft = Math.max(0, b.realStart - padLeft)
            }
        }

        /* Initial position requires layout to be complete — defer one frame so
         * cloned slides have their offsets calculated. */
        const raf = requestAnimationFrame(setInitial)

        const releaseSnap = () => {
            snapping = false
        }

        /* Snap once scroll has settled — i.e., no scroll events for SETTLE_MS.
         * Snapping mid-smooth-scroll cancels the browser's in-flight animation
         * (because behavior:'auto' takes over), producing a jarring interrupt
         * for arrow-click navigation. Waiting for settle makes the wrap clean
         * for both arrow-driven smooth-scrolls and user drags (drag fires the
         * snap shortly after release, which feels instant). */
        const SETTLE_MS = 80
        const trySnap = () => {
            if (snapping) return
            const b = computeBoundaries(node, slidesLength)
            if (!b) return
            const sl = node.scrollLeft
            if (sl < b.realStart) {
                snapping = true
                node.scrollTo({left: sl + b.realRangeWidth, behavior: 'auto'})
                requestAnimationFrame(releaseSnap)
            } else if (sl >= b.realEnd) {
                snapping = true
                node.scrollTo({left: sl - b.realRangeWidth, behavior: 'auto'})
                requestAnimationFrame(releaseSnap)
            }
        }

        let settleTimer = null
        const onScroll = () => {
            if (snapping) return
            if (settleTimer) clearTimeout(settleTimer)
            settleTimer = setTimeout(trySnap, SETTLE_MS)
        }

        node.addEventListener('scroll', onScroll, {passive: true})
        const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(setInitial)
        if (ro) ro.observe(node)

        return () => {
            cancelAnimationFrame(raf)
            if (settleTimer) clearTimeout(settleTimer)
            node.removeEventListener('scroll', onScroll)
            if (ro) ro.disconnect()
        }
    }, [enabled, slidesLength, scrollRef])
}
